import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApolloLink } from '@apollo/client';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import { combineLatest, Observable } from 'rxjs';

import { PaymentDetailsFactory } from 'app/factories';
import { ApolloQueryResult, mapToQueryResult } from 'app/graphql/apollo-query-result';
import {
    EveryPayRequestCountry,
    EveryPayRequestForUsergroupAddMoneyMutation,
    EveryPayRequestForUsergroupAddMoneyMutationGQL,
    EveryPayRequestLocale,
    PaymentDetailsQuery,
    PurchasePaymentStatusQuery,
    PurchasePaymentStatusQueryGQL,
    PurchasePaymentStatusQueryVariables,
} from 'app/graphql/generated/graphql-types';
import { NotificationsService } from 'app/services/notifications';

export enum BankEnum {
    swedbank,
    seb,
    luminor,
    danske,
    lhv,
    coop,
    cardpayment,
}

@Injectable()
export class PaymentService {
    private notificationsService = inject(NotificationsService);
    private apollo = inject(Apollo);
    private paymentDetailsFactory = inject(PaymentDetailsFactory);
    private route = inject(ActivatedRoute);
    private translateService = inject(TranslateService);

    getEveryPayRequestForUsergroupAddMoney(
        accountId: number,
        amount: number,
        country: EveryPayRequestCountry,
        returnUrl = '',
    ): Observable<ApolloLink.Result<EveryPayRequestForUsergroupAddMoneyMutation>> {
        return new EveryPayRequestForUsergroupAddMoneyMutationGQL(this.apollo).mutate({
            variables: {
                accountId,
                amount,
                country,
                returnUrl,
                requestToken: false,
                tokenAgreement: 'unscheduled',
                locale: this.translateService.getCurrentLang().substring(0, 2) as EveryPayRequestLocale,
            },
        });
    }

    pollPurchasePaymentStatus(
        variables: PurchasePaymentStatusQueryVariables,
        pollInterval = 5000,
    ): Observable<ApolloQueryResult<PurchasePaymentStatusQuery>> {
        return new PurchasePaymentStatusQueryGQL(this.apollo)
            .watch({
                variables: variables,
                fetchPolicy: 'network-only',
                pollInterval,
            })
            .valueChanges.pipe(mapToQueryResult());
    }

    getPaymentDetails(): Observable<ApolloQueryResult<PaymentDetailsQuery>> {
        return this.paymentDetailsFactory.getPaymentDetails();
    }

    getSuccessParams(amount: number): { paymentSuccess: boolean; amount: number } {
        return {
            paymentSuccess: true,
            amount,
        };
    }

    handleQueryParams(): void {
        const params = this.route.snapshot.queryParams;
        if (params.paymentSuccess) {
            combineLatest([
                this.translateService.get('profile.usergroup.add_money.payment_success_title'),
                this.translateService.get('profile.usergroup.add_money.payment_success_content', {
                    value: params.amount,
                }),
            ])
                .toPromise()
                .then((results: string[]) => {
                    this.notificationsService.success(results[0], results[1]);
                });
        } else if (params.paymentCanceled) {
            this.translateService
                .get('profile.usergroup.add_money.payment_cancel')
                .toPromise()
                .then((translation: string) => {
                    this.notificationsService.info(translation);
                });
        }
    }
}
